import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Chip,
  OutlinedInput,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import MpgInput from "../../../../../CommonFormComp/MpgInput";
import MpgItem from "../../../../../CommonFormComp/MpgItem";
import MpgFileUpload from "../../../../../fileHandling/MpgFileUpload";
import { managePDViewPath } from "../../../../../routes/routePaths";
import MpgErrorDialog from "../../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../../common/dialog/MpgLoadingDialog";
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useCreateProgramDescriptorMutation,
  useUploadProgramDescriptorFileMutation,
  useViewProgrammeDescriptorListMutation,
  useGetProgramDescriptorOwnersQuery,
} from "../../../../../reactQueries/AppApi/rqHooksForProgrammeDescriptor";
import useTrackUserActivity from "../../../../../userActivityTracking/useTrackUserActivity";
import { getAccessTypes } from "../../../../../hooksAndFunctions/functions/getAccessTypes";
import { createProgrammeDescriptorValidation } from "./createProgrammeDescriptorValidation";

const SelectSpinner = () => (
  <Box
    sx={{
      position: "absolute",
      right: 32,
      top: "50%",
      transform: "translateY(-50%)",
      display: "flex",
      alignItems: "center",
      pointerEvents: "none",
      zIndex: 1,
    }}
  >
    <CircularProgress size={16} color="inherit" />
  </Box>
);

const fixedSelectSx = {
  textAlign: "left",
  height: "40px",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "0px 8px",
    overflow: "hidden",
  },
};

const baseMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

const bottomMenuProps = (ref) => ({
  ...baseMenuProps,
  PaperProps: {
    style: { width: ref.current?.offsetWidth ?? "auto" },
    sx: {
      maxHeight: "200px",
      overflowY: "auto",
      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
      borderRadius: "4px",
      mt: 0.5,
    },
  },
  MenuListProps: { sx: { padding: 0 } },
  modifiers: [
    { name: "flip", enabled: false },
    { name: "preventOverflow", enabled: false },
  ],
});

const CreateProgrammeDescriptor = () => {
  const navigate = useNavigate();
  const resourcesCMSData = useOutletContext();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    emailAddress,
    clientPortalAdminRoleId,
    isEnterpriseAdmin,
    id: userId,
  } = loggedInUser;

  const { clientPortalAdminType } = getAccessTypes();
  const { hasA1Access } = clientPortalAdminType;
  const canViewNewFields = isEnterpriseAdmin || hasA1Access;

  const languageRef = useRef(null);
  const ownerRef = useRef(null);
  const durationRef = useRef(null);

  const returnLabel = (labelArray, key) => {
    if (labelArray?.length > 0 && key) {
      return labelArray.find((x) => x.Key === key)?.Phrase || "";
    }
    return "";
  };

  const fetchMngResoRepoDtlsLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["ManageResourceRepoDetails"]) {
      return returnLabel(
        resourcesCMSData.pageContent["ManageResourceRepoDetails"].Labels,
        key
      );
    }
    return "";
  };

  const fetchMngResoRepoListLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["ManageResourceRepoList"]) {
      return returnLabel(
        resourcesCMSData.pageContent["ManageResourceRepoList"].Labels,
        key
      );
    }
    return "";
  };

  const { data: masterData, isLoading: isMasterDataLoading } =
    useGetProgramDescriptorOwnersQuery();

  const owners = masterData?.Owners || [];
  const countryOptions = masterData?.Countries || [];
  const languageOptions = Array.from(
    new Map(
      (masterData?.Languages || []).map((lang) => [lang.LocaleName, lang])
    ).values()
  );
  const durationOptions = masterData?.Durations || [];
  const countryNames = useMemo(
    () => countryOptions.map((c) => c.CountryName),
    [countryOptions]
  );

  const { schema } = createProgrammeDescriptorValidation();

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      pdTitle: "",
      countries: [],
      language: "",
      owner: "",
      duration: [],
      pdFile: [],
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const {
    watch,
    control,
    formState: { errors },
    handleSubmit,
  } = rhfFormInstance;

  const inputPDTitle = watch("pdTitle");
  const watchedFile = watch("pdFile");
  const hasFile = Array.isArray(watchedFile) && watchedFile.length > 0;

  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);

  useEffect(() => {
    if (hasFile) setHasSubmitAttempted(false);
  }, [hasFile]);

  const { mutate: getPDList, data: PDData } =
    useViewProgrammeDescriptorListMutation();

  useEffect(() => {
    getPDList({
      loggedInUserId: userId,
      roleId: clientPortalAdminRoleId,
      isEnterpriseAdmin,
    });
  }, [loggedInUser]);

  const filteredPDListForStandardType =
    PDData?.data?.filter(
      (item) => item?.ProgramDescripitorType === "Standard"
    ) || [];

  const {
    mutate: createProgramDescriptor,
    isError: isErrorOnCreate,
    isLoading: isLoadingOnCreate,
    isSuccess: isSuccessOnCreate,
    error: errorOnCreate,
  } = useCreateProgramDescriptorMutation();

  const {
    mutate: uploadPDFile,
    isError: isErrorOnPDFileUpload,
    isLoading: isLoadingOnPDFileUpload,
    isSuccess: isSuccessOnPDFileUpload,
    data: dataOnPDFileUpload,
    error: errorOnPDFileUpload,
  } = useUploadProgramDescriptorFileMutation();

  const [bodyDataForCreate, setBodyDataForCreate] = useState({});

  const onFormSubmit = (e) => {
    setHasSubmitAttempted(true);
    handleSubmit(onSubmit)(e);
  };

  const onSubmit = (data) => {
    // Block submission if no file
    if (!hasFile) return;

    const body = {
      ProgramDescripitorTitle: data.pdTitle || "",
      IsActive: true,
      CreatedBy: emailAddress || "",
      ProgramDescripitorType: "Standard",
      ProgramDescripitorURL: "",
      ProgramDescripitorFileName: "",
      ProgramDescripitorFileGuid: "",
      ProgramDescripitorFileSize: "",
      Countries: data.countries || [],
      Language: data.language || "",
      ResourceOwner: data.owner || "",
      Durations: data.duration || [],
    };

    setBodyDataForCreate(body);

    const file = data.pdFile[0];
    const bodyData = {
      ...body,
      ProgramDescripitorFileName: file.name || "",
      ProgramDescripitorFileSize: file.size || "",
    };
    setBodyDataForCreate(bodyData);
    uploadPDFile({ file });
  };

  useEffect(() => {
    if (dataOnPDFileUpload?.data?.IsSuccess) {
      const bodyData = {
        ...bodyDataForCreate,
        ProgramDescripitorURL: dataOnPDFileUpload.data.ProgramDescripitorURL,
        ProgramDescripitorFileGuid: dataOnPDFileUpload.data.Id,
      };
      createProgramDescriptor({ body: bodyData });
    }
  }, [dataOnPDFileUpload?.data?.IsSuccess]);

  useEffect(() => {
    if (isSuccessOnCreate) navigate(managePDViewPath);
  }, [isSuccessOnCreate]);

  const standardTitles = filteredPDListForStandardType.map(
    (item) => item?.ProgramDescripitorTitle
  );
  const [standardPDTitleFieldErrorMsg, setStandardPDTitleFieldErrorMsg] =
    useState("");

  useEffect(() => {
    if (standardTitles.includes(inputPDTitle?.trim())) {
      setStandardPDTitleFieldErrorMsg(
        fetchMngResoRepoDtlsLabel(
          "ManageResourceRepoDetails.ErrorMsgStandardResourceExist"
        ) || "This standard resource title already exists in records."
      );
    } else {
      setStandardPDTitleFieldErrorMsg("");
    }
  }, [inputPDTitle, PDData]);

  const { trackActivity } = useTrackUserActivity();
  useEffect(() => {
    if (isSuccessOnPDFileUpload) {
      trackActivity({
        activitySuccess: true,
        actionType: "Uploaded",
        itemId: dataOnPDFileUpload?.data?.Id || "",
      });
    }
  }, [isSuccessOnPDFileUpload]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {resourcesCMSData?.pageContent?.["ManageResourceRepoDetails"]?.Title ||
          "Upload New Standard Resource"}
      </Typography>

      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={managePDViewPath}
        >
          {fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.Resources") ||
            "Resources"}
        </Button>
      </Stack>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={onFormSubmit}
      >
        <MpgItem>
          <MpgInput
            rhfFormInstance={rhfFormInstance}
            name="pdTitle"
            label={
              fetchMngResoRepoDtlsLabel(
                "ManageResourceRepoDetails.StandardResourceTitle"
              ) || "Standard Resource Title"
            }
            fullWidth
          />

          <FormHelperText error={!!standardPDTitleFieldErrorMsg}>
            {standardPDTitleFieldErrorMsg}
          </FormHelperText>

          {canViewNewFields && (
            <>
              {/* Row 1 — Countries + Language */}
              <Grid
                container
                spacing={2}
                sx={{ mt: 1 }}
                alignItems="flex-start"
              >
                {/* Countries */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="countries"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={countryNames}
                          value={field.value || []}
                          onChange={(_, newValue) => {
                            field.onChange(newValue);
                            rhfFormInstance.trigger("countries");
                          }}
                          disabled={isMasterDataLoading}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "40px",
                              maxHeight: "40px",
                              overflow: "hidden",
                              flexWrap: "nowrap",
                              padding: "0px 8px !important",
                            },
                            "& .MuiAutocomplete-tag": {
                              maxWidth: "calc(50% - 8px)",
                            },
                          }}
                          renderOption={(props, option, { selected }) => (
                            <MenuItem {...props} dense>
                              <Checkbox
                                checked={selected}
                                size="small"
                                sx={{ py: 0.5 }}
                              />
                              <ListItemText primary={option} />
                            </MenuItem>
                          )}
                          renderTags={(selected, getTagProps) =>
                            selected.map((val, index) => (
                              <Chip
                                key={val}
                                label={val}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label={
                                fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Countries"
                                ) + " *"
                              }
                              placeholder={
                                field.value?.length === 0 ? "Countries" : ""
                              }
                              error={!!errors.countries}
                              helperText={errors.countries?.message}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{
                                ...params.inputProps,
                                style: { fontSize: "13px", padding: "0px" },
                              }}
                            />
                          )}
                          ListboxProps={{ sx: { maxHeight: 280 } }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* Language */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }} ref={languageRef}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="language"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.language}
                        >
                          <InputLabel shrink>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.Language"
                            )}{" "}
                            *
                          </InputLabel>
                          <Select
                            {...field}
                            displayEmpty
                            disabled={isMasterDataLoading}
                            onChange={(e) => {
                              field.onChange(e);
                              rhfFormInstance.trigger("language");
                            }}
                            input={
                              <OutlinedInput
                                notched
                                label={`${fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Language"
                                )} *`}
                              />
                            }
                            sx={fixedSelectSx}
                            MenuProps={bottomMenuProps(languageRef)}
                            renderValue={(val) =>
                              isMasterDataLoading ? (
                                <Typography fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.LoadingLanguages"
                                  )}
                                </Typography>
                              ) : val ? (
                                <Typography fontSize={13}>{val}</Typography>
                              ) : (
                                <Typography color="text.disabled" fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.Language"
                                  )}
                                </Typography>
                              )
                            }
                          >
                            {languageOptions.map((lang) => (
                              <MenuItem
                                key={lang.LocaleKey}
                                value={lang.LocaleName}
                                dense
                                sx={{ fontSize: 13, py: 0.75 }}
                              >
                                {lang.LocaleName}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.language && (
                            <FormHelperText>
                              {errors.language.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Row 2 — Owner + Duration */}
              <Grid
                container
                spacing={2}
                sx={{ mt: 1 }}
                alignItems="flex-start"
              >
                {/* Owner */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }} ref={ownerRef}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="owner"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.owner}
                        >
                          <InputLabel shrink>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.Owner"
                            )}{" "}
                            *
                          </InputLabel>
                          <Select
                            {...field}
                            displayEmpty
                            disabled={isMasterDataLoading}
                            onChange={(e) => {
                              field.onChange(e);
                              rhfFormInstance.trigger("owner");
                            }}
                            input={
                              <OutlinedInput
                                notched
                                label={`${fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Owner"
                                )} *`}
                              />
                            }
                            sx={fixedSelectSx}
                            MenuProps={bottomMenuProps(ownerRef)}
                            renderValue={(val) =>
                              isMasterDataLoading ? (
                                <Typography fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.LoadingOwners"
                                  )}
                                </Typography>
                              ) : val ? (
                                <Typography fontSize={13}>{val}</Typography>
                              ) : (
                                <Typography color="text.disabled" fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.Owner"
                                  )}
                                </Typography>
                              )
                            }
                          >
                            {owners.map((owner) => (
                              <MenuItem
                                key={owner}
                                value={owner}
                                dense
                                sx={{ fontSize: 13, py: 0.75 }}
                              >
                                {owner}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.owner && (
                            <FormHelperText>
                              {errors.owner.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Grid>

                {/* Duration */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }} ref={durationRef}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="duration"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.duration}
                        >
                          <InputLabel shrink>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.Duration"
                            )}{" "}
                            *
                          </InputLabel>
                          <Select
                            multiple
                            displayEmpty
                            value={field.value || []}
                            onChange={(e) => {
                              field.onChange(e);
                              rhfFormInstance.trigger("duration"); 
                            }}
                            disabled={isMasterDataLoading}
                            input={
                              <OutlinedInput
                                notched
                                label={`${fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Duration"
                                )} *`}
                              />
                            }
                            sx={fixedSelectSx}
                            MenuProps={bottomMenuProps(durationRef)}
                            renderValue={(selected) =>
                              isMasterDataLoading ? (
                                <Typography fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.LoadingDurations"
                                  )}
                                </Typography>
                              ) : selected.length === 0 ? (
                                <Typography color="text.disabled" fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.Duration"
                                  )}
                                </Typography>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    gap: 0.5,
                                    overflow: "hidden",
                                  }}
                                >
                                  {selected.map((val) => (
                                    <Chip
                                      key={val}
                                      label={val}
                                      size="small"
                                      onDelete={() =>
                                        field.onChange(
                                          field.value.filter((v) => v !== val)
                                        )
                                      }
                                      onMouseDown={(e) => e.stopPropagation()}
                                    />
                                  ))}
                                </Box>
                              )
                            }
                          >
                            {durationOptions.map((dur) => (
                              <MenuItem
                                key={dur}
                                value={dur}
                                dense
                                sx={{ fontSize: 13, py: 0.75 }}
                              >
                                <Checkbox
                                  checked={(field.value || []).includes(dur)}
                                  size="small"
                                  sx={{ py: 0.5 }}
                                />
                                <ListItemText
                                  primary={dur}
                                  primaryTypographyProps={{ fontSize: 13 }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.duration && (
                            <FormHelperText>
                              {errors.duration.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          {/* File Upload */}
          <Box sx={{ mt: 2 }}>
            <MpgFileUpload
              name="pdFile"
              label={
                fetchMngResoRepoDtlsLabel(
                  "ManageResourceRepoDetails.UploadFile"
                ) || "Upload file"
              }
              rhfFormInstance={rhfFormInstance}
              persistantMsg={
                fetchMngResoRepoDtlsLabel(
                  "ManageResourceRepoDetails.AllowedFile"
                ) || "Only pdf/ppt file type is allowed"
              }
            />
            {/* ✅ Manual file error — no RHF/yup involvement */}
            {hasSubmitAttempted && !hasFile && (
              <FormHelperText error>
                {fetchMngResoRepoDtlsLabel(
                  "ManageResourceRepoDetails.FileRequired"
                ) || "This field is required"}
              </FormHelperText>
            )}
          </Box>
        </MpgItem>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-evenly"
          mt={3}
        >
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!!standardPDTitleFieldErrorMsg || isMasterDataLoading}
            startIcon={
              isMasterDataLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isMasterDataLoading
              ? "Loading..."
              : fetchMngResoRepoDtlsLabel(
                  "ManageResourceRepoDetails.CreateStandardResource"
                ) || "Create Standard Resource"}
          </Button>
        </Stack>
      </Box>

      {isErrorOnPDFileUpload &&
      errorOnPDFileUpload?.response?.data?.ErrorCode === "104" ? (
        <MpgErrorDialog
          open={!!isErrorOnPDFileUpload}
          title={fetchMngResoRepoDtlsLabel(
            "ManageResourceRepoDetails.ErrorMsgFileScanFailed"
          )}
        />
      ) : (
        <MpgErrorDialog open={!!isErrorOnPDFileUpload} />
      )}

      {isErrorOnCreate &&
      errorOnCreate?.response?.data === "ProgrammeDescriptorTitleExists" ? (
        <MpgErrorDialog
          open={!!isErrorOnCreate}
          title={fetchMngResoRepoDtlsLabel(
            "ManageResourceRepoDetails.ErrorMsgDuplicateResourceTitle"
          )}
          content={`${fetchMngResoRepoListLabel(
            "ManageResourceRepoList.ResourceTitle"
          )} ${watch("pdTitle")} ${fetchMngResoRepoDtlsLabel(
            "ManageResourceRepoDetails.ErrorMsgResourceExist"
          )}`}
        />
      ) : (
        <MpgErrorDialog open={!!isErrorOnCreate} />
      )}

      <MpgLoadingDialog
        open={!!isLoadingOnCreate || !!isLoadingOnPDFileUpload}
      />
    </Box>
  );
};

export default CreateProgrammeDescriptor;
